import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SxProps, Theme } from '@mui/system';
import { FC } from 'react';
import { CfCheckboxList } from '../../../../../cf-ui/CheckboxList/CfCheckboxList';
import {
  CfCheckbox,
  CfCheckboxIcon,
  CheckboxItem,
} from '../../../../../cf-ui/CheckboxList/components/Checkbox/CfCheckbox';
import { DeviceType, useBreakpoint } from '../../../../../hooks/useBreakpoint';
import { StyledContainer, StyledWrapper } from './LocationCheckboxList.styles';

export interface LocationCheckboxItem {
  customerId: string;
  selected: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
}

export interface LocationCheckboxListProps {
  items: LocationCheckboxItem[];
  onChange?: (items: LocationCheckboxItem[]) => void;
  selectAllIcon?: CfCheckboxIcon;
  onSelectAllChecked?: (checked: boolean) => void;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  greyBackground?: boolean;
  dataTestId?: string;
  dataAltTestId?: string;
  gap?: number | string | ((theme: Theme) => string | number);
}

const selectAllValue = 'select-all-location-checkboxes';

const LocationCheckboxList: FC<LocationCheckboxListProps> = (props: LocationCheckboxListProps) => {
  const { items: locationOptions, onChange, onSelectAllChecked, isLoading, dataTestId, dataAltTestId, gap } = props;
  const { deviceType } = useBreakpoint();

  const selectAllIcon = props.items.filter((i) => i.selected).length === props.items.length ? 'check' : 'minus';
  const selectAllChecked = locationOptions.filter((l) => l.selected).length > 0;

  const handleSelectAll = (item: CheckboxItem) => {
    onSelectAllChecked?.(item.checked);
    const newOptions = locationOptions.map((o) => {
      if (o.disabled) return o;
      else return { ...o, selected: item.checked };
    });
    onChange?.(newOptions);
  };

  const handleOnCheckedChange = (item: CheckboxItem) => {
    if (item.additionalValue === selectAllValue) return handleSelectAll(item);

    const optionIndex = locationOptions.findIndex((o) => o.customerId === item.additionalValue);
    if (optionIndex >= 0) {
      const option = locationOptions[optionIndex];

      const newOptions = [...locationOptions];
      newOptions[optionIndex] = { ...option, selected: !option.selected };

      const checkedLength = newOptions.filter((o) => o.selected).length;
      onSelectAllChecked?.(checkedLength === newOptions.length);
      onChange?.(newOptions);
    }
  };

  const checkboxItems = locationOptions.map((option) => {
    return {
      label: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='paragraphSmall' sx={{ color: (theme) => theme.palette.grey[600] }}>
            {option.label}
          </Typography>
          <Box sx={{ height: (theme) => theme.spacing(0.5) }} />
        </Box>
      ),
      checked: option.selected,
      disabled: option.disabled,
      additionalValue: option.customerId,
    } as CheckboxItem;
  });

  const selectAll = {
    label: (
      <Typography variant='responsiveParagraph' sx={{ color: (theme) => theme.palette.grey[700] }}>
        Select all
      </Typography>
    ),
    checked: selectAllChecked,
    name: 'Select all',
    additionalValue: selectAllValue,
  } as CheckboxItem;

  const greyBackground = props.greyBackground !== false;
  return (
    <>
      {isLoading ? (
        <>
          <StyledContainer
            greyBackground={greyBackground}
            sx={{
              height: (theme) => theme.spacing(5.25),
              pt: (theme) => ({ xl: theme.spacing(1.625) }),
            }}
          >
            <Stack direction='row'>
              <Skeleton
                sx={{
                  width: (theme) => ({ xs: theme.spacing(3.125), xl: theme.spacing(2) }),
                  height: (theme) => ({ xs: theme.spacing(3.125), xl: theme.spacing(2) }),
                }}
                variant='rectangular'
              />
              <Typography variant='paragraphBold' width='70%'>
                <Skeleton sx={{ height: (theme) => theme.spacing(2), ml: 1 }} variant='rectangular' />
              </Typography>
            </Stack>
          </StyledContainer>
          <Divider sx={{ borderColor: (theme: Theme) => theme.palette.common.white }} />
          <StyledContainer greyBackground={greyBackground}>
            {[1, 2, 3, 4].map((i) => (
              <LocationCheckboxLoadingSkeleton key={i} />
            ))}
          </StyledContainer>
        </>
      ) : (
        <StyledWrapper
          greyBackground={greyBackground}
          sx={{
            overflowAnchor: 'none',
            height: (theme) => ({
              xs: `min(calc(50vh - ${theme.spacing(6.5)}) , ${theme.spacing(44.5)})`,
            }),
            ...props.sx,
          }}
        >
          {checkboxItems.length > 1 && (
            <>
              <StyledContainer greyBackground={greyBackground} sx={{ height: (theme) => theme.spacing(5.25) }}>
                <CfCheckbox
                  dataTestId={dataTestId}
                  item={selectAll}
                  onCheckedChanged={handleSelectAll}
                  icon={selectAllIcon}
                  size={deviceType === DeviceType.Desktop ? 'small' : 'medium'}
                />
              </StyledContainer>
              <Divider sx={{ borderColor: (theme: Theme) => theme.palette.common.white }} />
            </>
          )}
          <StyledContainer greyBackground={greyBackground}>
            <CfCheckboxList
              items={checkboxItems}
              length={checkboxItems.length}
              onCheckedChange={handleOnCheckedChange}
              variant='primary'
              icon='check'
              dataTestId='location-checkbox-input'
              dataAltTestId={dataAltTestId}
              checkboxSize={deviceType === DeviceType.Desktop ? 'small' : 'medium'}
              gap={gap}
            />
            {((isLoading === false && locationOptions.length === 0) || props.items.length === 0) && (
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Typography variant='paragraph'>No results found</Typography>
              </Box>
            )}
          </StyledContainer>
        </StyledWrapper>
      )}
    </>
  );
};

export default LocationCheckboxList;

export const LocationCheckboxLoadingSkeleton = (): JSX.Element => {
  const theme = useTheme();
  return (
    <Box sx={{ paddingBottom: theme.spacing(0.5) }}>
      <Stack direction='row' sx={{ height: 18 }}>
        <Skeleton
          variant='rectangular'
          sx={{
            width: { xs: theme.spacing(3.125), xl: theme.spacing(2) },
            height: { xs: theme.spacing(3.125), xl: theme.spacing(2) },
          }}
        />
        <Typography variant='paragraphBold' width='70%'>
          <Skeleton sx={{ ml: 1 }} variant='rectangular' height={theme.spacing(2)} />
        </Typography>
      </Stack>
      <Typography variant='paragraph'>
        <Skeleton sx={{ ml: { xs: 4.125, xl: 3 }, mr: 6, mb: 0.5 }} variant='rectangular' height={theme.spacing(1.5)} />
      </Typography>
      <Typography variant='paragraph'>
        <Skeleton sx={{ ml: { xs: 4.125, xl: 3 }, mr: 8, mb: 0.5 }} variant='rectangular' height={theme.spacing(1.5)} />
      </Typography>
      <Typography variant='paragraph'>
        <Skeleton sx={{ ml: { xs: 4.125, xl: 3 }, mr: 4, mb: 0.5 }} variant='rectangular' height={theme.spacing(1.5)} />
      </Typography>
    </Box>
  );
};
